import {
  ErrorEnvelopeSchema,
  assertIJson,
  parseIJson,
  type ErrorCode,
  type ErrorEnvelope,
} from "@mind/contracts/browser";

type OutputSchema<Output> = {
  safeParse(input: unknown):
    | { readonly success: true; readonly data: Output }
    | { readonly success: false; readonly error: unknown };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly messageKey: string;
  readonly params: Readonly<Record<string, string | boolean>>;
  readonly requestId: string;

  constructor(status: number, error: ErrorEnvelope["error"]) {
    super(error.messageKey);
    this.name = "ApiError";
    this.status = status;
    this.code = error.code;
    this.messageKey = error.messageKey;
    this.params = error.params;
    this.requestId = error.requestId;
  }
}

export class ApiContractError extends Error {
  readonly status: number;

  constructor(status: number, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "ApiContractError";
    this.status = status;
  }
}

export type ApiRequestOptions = Omit<RequestInit, "body"> & {
  readonly body?: unknown;
};

function decodeJson(response: Response, text: string): unknown {
  try {
    return parseIJson(text);
  } catch (error) {
    throw new ApiContractError(response.status, "API returned malformed JSON", { cause: error });
  }
}

export async function apiRequest<Output>(
  schema: OutputSchema<Output>,
  path: string,
  options: ApiRequestOptions = {},
): Promise<Output> {
  const { body: requestBody, ...requestOptions } = options;
  const headers = new Headers(requestOptions.headers);
  if (!headers.has("accept")) headers.set("accept", "application/json");

  let body: BodyInit | undefined;
  if (requestBody !== undefined) {
    assertIJson(requestBody);
    headers.set("content-type", "application/json");
    body = JSON.stringify(requestBody);
  }

  const requestInit: RequestInit = body === undefined
    ? { ...requestOptions, headers }
    : { ...requestOptions, body, headers };
  const response = await fetch(path, requestInit);
  const payload = decodeJson(response, await response.text());

  if (!response.ok) {
    const envelope = ErrorEnvelopeSchema.safeParse(payload);
    if (!envelope.success) {
      throw new ApiContractError(response.status, "API returned a malformed error envelope", { cause: envelope.error });
    }
    throw new ApiError(response.status, envelope.data.error);
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiContractError(response.status, "API response violated its schema", { cause: parsed.error });
  }
  return parsed.data;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
